import React, { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, runTransaction, onSnapshot } from 'firebase/firestore';
import { auth, signInWithGoogle, db } from '../../firebase';
import PosterHeader from '../../components/header/PosterHeaderBar/PosterHeader';
import MatrixRain from '../../components/p5/MatrixRain';
import Footer from '../../components/footer/footer';
import '../PosterPage.css';

// ====== CONFIG (edit these) ======
const RECAPTCHA_SITE_KEY = 'Y6LcZhOcrAAAAAKcWWhtb-hMWZWiXqHJAFjTY-YTD'; // from Google
const API_ENDPOINT = '/api/solenoid_fire.php';            // Hostinger PHP
const FIRE_AT_COUNT = 3;                                  // when total clicks reach this, fire solenoid
const HOLD_MS_ON_TRIGGER = 15000;                         // 30s hold
const DISPLAY_DIGITS = 5;                                 // width of 7-seg display (pad with zeros)
// =================================

// ---------- Responsive SVG 7-segment (fits parent width) ----------
function SevenSegmentDisplay({ value, digits = 5 }) {
  // Segment order: A,B,C,D,E,F,G
  const MAP = {
    '0': [1,1,1,1,1,1,0],
    '1': [0,1,1,0,0,0,0],
    '2': [1,1,0,1,1,0,1],
    '3': [1,1,1,1,0,0,1],
    '4': [0,1,1,0,0,1,1],
    '5': [1,0,1,1,0,1,1],
    '6': [1,0,1,1,1,1,1],
    '7': [1,1,1,0,0,0,0],
    '8': [1,1,1,1,1,1,1],
    '9': [1,1,1,1,0,1,1],
    '-': [0,0,0,0,0,0,1],
    ' ': [0,0,0,0,0,0,0],
  };

  const onColor = '#f94144';
  const offColor = '#0f1a1a';
  const str = String(value).padStart(digits, '0').slice(-digits);

  // Smaller geometry for mobile + tighter gaps
  const segmentThickness = 10;
  const segmentLength = 42;
  const segmentGap = 6;

  const w = segmentLength + segmentThickness * 2;
  const h = segmentLength * 2 + segmentThickness * 3;
  const totalW = (w + segmentGap) * digits - segmentGap;

  const seg = (x, y, horiz, on, key) => (
    <rect
      key={key}
      x={x}
      y={y}
      width={horiz ? segmentLength : segmentThickness}
      height={horiz ? segmentThickness : segmentLength}
      rx={6}
      ry={6}
      fill={on ? onColor : offColor}
      style={on ? { filter: 'drop-shadow(0 0 5px rgba(249,65,68,0.8))' } : {}}
    />
  );

  const renderDigit = (ch, offsetX) => {
    const m = MAP[ch] || MAP[' '];
    return [
      seg(offsetX + segmentThickness, 0, true, m[0], `${offsetX}-A`),
      seg(offsetX + w - segmentThickness, segmentThickness, false, m[1], `${offsetX}-B`),
      seg(offsetX + w - segmentThickness, segmentThickness * 2 + segmentLength, false, m[2], `${offsetX}-C`),
      seg(offsetX + segmentThickness, segmentThickness * 2 + segmentLength * 2, true, m[3], `${offsetX}-D`),
      seg(offsetX, segmentThickness * 2 + segmentLength, false, m[4], `${offsetX}-E`),
      seg(offsetX, segmentThickness, false, m[5], `${offsetX}-F`),
      seg(offsetX + segmentThickness, segmentThickness + segmentLength, true, m[6], `${offsetX}-G`),
    ];
  };

  return (
    <svg
      // key trick: let the SVG scale to parent width
      width="100%"
      height="auto"
      viewBox={`0 0 ${totalW} ${h}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ display: 'block', background: 'transparent' }}
      aria-label={`Seven segment: ${str}`}
    >
      {str.split('').map((ch, i) => renderDigit(ch, i * (w + segmentGap)))}
    </svg>
  );
}

// Retro buttons
function RetroButton({ children, className = '', ...rest }) {
  return (
    <button
      {...rest}
      className={[
        'relative select-none',
        'px-6 py-3 rounded-xl font-extrabold tracking-wide',
        'text-white',
        'bg-gradient-to-b from-fuchsia-500 to-violet-700',
        'shadow-[0_10px_0_0_rgba(100,28,203,1),0_20px_30px_rgba(0,0,0,0.45)]',
        'border-2 border-fuchsia-300/70',
        'active:translate-y-1 active:shadow-[0_6px_0_0_rgba(100,28,203,1),0_12px_18px_rgba(0,0,0,0.45)]',
        'transition-transform duration-100',
        'hover:brightness-110',
        className,
      ].join(' ')}
      style={{ textShadow: '0 0 8px rgba(255,180,255,0.7)' }}
    >
      {children}
    </button>
  );
}
function GhostButton({ children, className = '', ...rest }) {
  return (
    <button
      {...rest}
      className={[
        'px-4 py-2 rounded-lg font-semibold',
        'text-fuchsia-200',
        'border border-fuchsia-400/60',
        'bg-black/20',
        'backdrop-blur-[2px]',
        'hover:bg-fuchsia-500/10 hover:text-fuchsia-100',
        'transition-colors',
        className
      ].join(' ')}
    >
      {children}
    </button>
  );
}

// ----------------------- MAIN PAGE -----------------------
function ButtonPage() {
  const [user, setUser] = useState(null);
  const [hasClicked, setHasClicked] = useState(false);
  const [clickedCount, setClickedCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const recaptchaReady = useRef(false);

  // Load reCAPTCHA v3
  useEffect(() => {
    if (window.grecaptcha) { recaptchaReady.current = true; return; }
    const s = document.createElement('script');
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    s.async = true;
    s.onload = () => { recaptchaReady.current = true; };
    document.head.appendChild(s);
  }, []);

  // Auth & live count
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const userRef = doc(db, 'clickedUsers', currentUser.uid);
        const docSnap = await getDoc(userRef);
        setHasClicked(docSnap.exists());
      } else {
        setHasClicked(false);
      }
    });

    const globalDocRef = doc(db, 'globalStats', 'clickCounts');
    const unsubscribeCount = onSnapshot(globalDocRef, async (snapshot) => {
      const count = snapshot.exists() ? (snapshot.data().totalClicks ?? 0) : 0;
      setClickedCount(count);
      if (count === FIRE_AT_COUNT) {
        try { await triggerSolenoid({ cmd: 'HOLD', ms: HOLD_MS_ON_TRIGGER }); }
        catch (err) { console.error('Failed to trigger solenoid:', err); }
      }
    });

    return () => { unsubscribeAuth(); unsubscribeCount(); };
  }, []);

  const handleSignIn = async () => { try { await signInWithGoogle(); } catch (e) { console.error(e); } };
  const handleSignOut = async () => { try { await signOut(auth); } catch (e) { console.error(e); } };

  const handleButtonClick = async () => {
    if (!user || hasClicked || busy) return;
    const userRef = doc(db, 'clickedUsers', user.uid);
    const globalRef = doc(db, 'globalStats', 'clickCounts');
    setBusy(true);
    try {
      let newlyCreated = false;
      await runTransaction(db, async (tx) => {
        const userDoc = await tx.get(userRef);
        const globalDoc = await tx.get(globalRef);
        if (!userDoc.exists()) {
          tx.set(userRef, { clicked: true, at: new Date().toISOString() });
          if (globalDoc.exists()) tx.update(globalRef, { totalClicks: (globalDoc.data().totalClicks ?? 0) + 1 });
          else tx.set(globalRef, { totalClicks: 1 });
          newlyCreated = true;
        }
      });
      if (newlyCreated) { setHasClicked(true); setClickedCount((p) => p + 1); alert('Button clicked!'); }
    } catch (e) {
      console.error('Failed to record click with transaction:', e);
    } finally { setBusy(false); }
  };

  async function triggerSolenoid(opts) {
    try {
      await waitForRecaptcha();
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'fire_solenoid' });
  
      const params = new URLSearchParams();
      params.set('recaptcha_token', token);
      params.set('cmd', opts.cmd);
      if (opts.cmd === 'HOLD' && typeof opts.ms === 'number') params.set('ms', String(opts.ms));
  
      const res = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
        credentials: 'same-origin',
      });
  
      const text = await res.text();           // <-- read raw
      let data = {};
      try { data = JSON.parse(text); } catch(_) {}
  
      console.log('API status', res.status, 'body:', text);
  
      if (!res.ok || !data.ok) {
        alert(`API error: ${data.error || res.status}`);
        throw new Error(data.error || `HTTP ${res.status}`);
      }
    } catch (e) {
      console.error('triggerSolenoid error:', e);
      throw e;
    }
  }
  
  function waitForRecaptcha() {
    if (recaptchaReady.current && window.grecaptcha?.execute) return Promise.resolve();
    return new Promise((resolve) => {
      const i = setInterval(() => {
        if (recaptchaReady.current && window.grecaptcha?.execute) { clearInterval(i); resolve(); }
      }, 50);
    });
  }

  // ---- UI ----
  if (!user) {
    return (
      <div className="relative w-full min-h-screen">
        <MatrixRain />
        <div className="relative z-10 mt-16">
          <PosterHeader />
          <div className="flex flex-col items-center justify-center h-screen text-white px-4">
            <h1 className="text-3xl md:text-4xl mb-4 text-center">Please sign in with Google</h1>
            <RetroButton onClick={handleSignIn}>Sign In</RetroButton>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="relative w-full min-h-screen">
      <MatrixRain />
      <div className="relative z-10 mt-16">
        <PosterHeader />

        {/* Dark parent card — centered, responsive */}
        <div className="flex justify-center px-4 pb-12">
          <div
            className={[
              'w-full max-w-lg md:max-w-2xl',
              'bg-black/70 border border-fuchsia-500/30 rounded-3xl',
              'backdrop-blur-md overflow-hidden',
              'shadow-[0_0_40px_rgba(255,0,200,0.15)]',
              'p-5 md:p-8',
            ].join(' ')}
          >
            {/* Counter on top, centered */}
            <div className="flex flex-col items-center gap-2 md:gap-3">
              <div className="text-fuchsia-200 uppercase tracking-widest text-xs md:text-sm">
                Total Accounts Clicked
              </div>

              {/* Responsive container: fits phone width; change maxWidth to taste */}
              <div
                className="w-full mx-auto px-2"
                style={{ maxWidth: 'min(92vw, 420px)' }}
              >
                <SevenSegmentDisplay value={clickedCount} digits={DISPLAY_DIGITS} />
              </div>
            </div>

            {/* Main vote button */}
            <div className="mt-6 md:mt-8 flex justify-center">
              <RetroButton
                onClick={handleButtonClick}
                disabled={hasClicked || busy}
                className={(hasClicked || busy) ? 'opacity-60 cursor-not-allowed' : ''}
              >
                {hasClicked ? 'Already Clicked' : (busy ? 'Saving…' : 'Centre Button')}
              </RetroButton>
            </div>

            {/* Secondary controls */}
            <div className="mt-6 md:mt-8 flex flex-wrap justify-center gap-3">
              <GhostButton onClick={() => triggerSolenoid({ cmd: 'PULSE' }).catch(console.error)}>
                Test PULSE
              </GhostButton>
              <GhostButton onClick={() => triggerSolenoid({ cmd: 'HOLD', ms: HOLD_MS_ON_TRIGGER }).catch(console.error)}>
                Test HOLD 30s
              </GhostButton>
              <GhostButton onClick={() => triggerSolenoid({ cmd: 'OFF' }).catch(console.error)}>
                OFF
              </GhostButton>
            </div>

            {/* Sign out */}
            <div className="mt-8 md:mt-10 flex justify-center">
              <GhostButton onClick={handleSignOut}>Sign Out</GhostButton>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ButtonPage;
